// Enhanced Payment Management System (tenant-scoped for SaaS)
import { 
  ref, 
  push, 
  set, 
  get, 
  update, 
  query, 
  orderByChild, 
  equalTo
} from 'firebase/database';
import { db } from './config';
import { auth } from './config';
import { calculateMembershipFees, isMembershipPaused, toDate } from '../utils/membershipFees';

function tenantPath(tenantId, ...segments) {
  return ['tenants', tenantId, ...segments].filter(Boolean).join('/');
}

// Payment Types
export const PAYMENT_TYPES = {
  FULL: 'full',
  PARTIAL: 'partial',
  LATE_FEE: 'late_fee',
  REFUND: 'refund',
  ADJUSTMENT: 'adjustment'
};

// Membership Status
export const MEMBERSHIP_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  FROZEN: 'frozen'
};

// Attendance Status
export const ATTENDANCE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  FROZEN: 'frozen'
};

// Record a payment with balance tracking
export const recordPaymentWithBalance = async (tenantId, memberId, paymentData) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    // Get current member data
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    const currentBalance = member.currentBalance || 0;
    const monthlyFee = parseFloat(member.fees.replace(/[^\d.]/g, '') || 0);
    const paymentAmount = parseFloat(paymentData.amount);

    // Calculate new balance
    const newBalance = Math.max(0, currentBalance - paymentAmount);
    
    // Determine payment type
    let paymentType = PAYMENT_TYPES.FULL;
    if (paymentAmount < monthlyFee) {
      paymentType = PAYMENT_TYPES.PARTIAL;
    } else if (paymentAmount > monthlyFee) {
      paymentType = PAYMENT_TYPES.PARTIAL; // Overpayment
    }

    // Create payment record
    const payment = {
      ...paymentData,
      memberId,
      memberName: member.fullName,
      paymentDate: new Date().toISOString(),
      timestamp: Date.now(),
      status: 'completed',
      type: paymentType,
      balanceBefore: currentBalance,
      balanceAfter: newBalance,
      monthlyFee,
      paymentAmount
    };

    // Save payment to database
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    const newPaymentRef = push(paymentsRef);
    await set(newPaymentRef, payment);

    // Update member's balance and payment info
    const memberUpdates = {
      currentBalance: newBalance,
      lastPaymentDate: payment.paymentDate,
      lastPaymentAmount: paymentAmount,
      paymentStatus: newBalance === 0 ? 'paid' : 'partial',
      totalPayments: (member.totalPayments || 0) + paymentAmount
    };

    // Update next due date if balance is fully paid
    if (newBalance === 0) {
      const membershipDuration = member.membershipDuration || 'monthly';
      memberUpdates.nextDueDate = calculateNextDueDateAfterPayment(
        member.nextDueDate || new Date().toISOString(), 
        membershipDuration
      );
    }

    await update(memberRef, memberUpdates);

    return {
      success: true,
      paymentId: newPaymentRef.key,
      newBalance,
      paymentType,
      message: `Payment recorded successfully. New balance: ₹${newBalance}`
    };

  } catch (error) {
    console.error('Error in recordPaymentWithBalance:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while recording the payment'
    };
  }
};

// Handle membership freeze (medical, travel, etc.)
export const freezeMembership = async (tenantId, memberId, freezeData) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    const freezeRecord = {
      ...freezeData,
      startDate: freezeData.startDate || new Date().toISOString(),
      endDate: freezeData.endDate,
      reason: freezeData.reason || 'other',
      approvedBy: auth.currentUser.uid,
      approvedDate: new Date().toISOString(),
      status: 'active'
    };

    // Calculate freeze duration and adjust billing
    const startDate = new Date(freezeRecord.startDate);
    const endDate = new Date(freezeRecord.endDate);
    const freezeDurationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Adjust next due date by freeze duration
    const adjustedDueDate = new Date(member.nextDueDate);
    adjustedDueDate.setDate(adjustedDueDate.getDate() + freezeDurationDays);

    const memberUpdates = {
      attendanceStatus: ATTENDANCE_STATUS.FROZEN,
      nextDueDate: adjustedDueDate.toISOString(),
      membershipStatus: MEMBERSHIP_STATUS.FROZEN
    };

    // Add to freeze history
    const currentFreezeHistory = member.freezeHistory || [];
    memberUpdates.freezeHistory = [...currentFreezeHistory, freezeRecord];

    await update(memberRef, memberUpdates);

    return {
      success: true,
      message: `Membership frozen successfully until ${freezeRecord.endDate}`,
      adjustedDueDate: adjustedDueDate.toISOString()
    };

  } catch (error) {
    console.error('Error in freezeMembership:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while freezing membership'
    };
  }
};

// Resume frozen membership (from FROZEN status)
export const resumeFrozenMembership = async (tenantId, memberId) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    
    if (member.attendanceStatus !== ATTENDANCE_STATUS.FROZEN) {
      throw new Error('Membership is not currently frozen');
    }

    // Update status
    const memberUpdates = {
      attendanceStatus: ATTENDANCE_STATUS.ACTIVE,
      membershipStatus: MEMBERSHIP_STATUS.ACTIVE
    };

    // Mark current freeze as completed
    if (member.freezeHistory && member.freezeHistory.length > 0) {
      const updatedFreezeHistory = member.freezeHistory.map(freeze => {
        if (freeze.status === 'active') {
          return { ...freeze, status: 'completed', endDate: new Date().toISOString() };
        }
        return freeze;
      });
      memberUpdates.freezeHistory = updatedFreezeHistory;
    }

    await update(memberRef, memberUpdates);

    return {
      success: true,
      message: 'Membership resumed successfully'
    };

  } catch (error) {
    console.error('Error in resumeMembership:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while resuming membership'
    };
  }
};

// Handle no-show scenarios
export const handleNoShowMonth = async (tenantId, memberId, month, year, action) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    const monthlyFee = parseFloat(member.fees.replace(/[^\d.]/g, '') || 0);

    switch (action) {
      case 'charge_full':
        // Charge full month fee
        const newBalance = (member.currentBalance || 0) + monthlyFee;
        await update(memberRef, {
          currentBalance: newBalance,
          paymentStatus: 'overdue'
        });
        return {
          success: true,
          message: `Full month fee charged. New balance: ₹${newBalance}`,
          newBalance
        };

      case 'charge_half':
        // Charge half month fee
        const halfFee = monthlyFee / 2;
        const newBalanceHalf = (member.currentBalance || 0) + halfFee;
        await update(memberRef, {
          currentBalance: newBalanceHalf,
          paymentStatus: 'partial'
        });
        return {
          success: true,
          message: `Half month fee charged. New balance: ₹${newBalanceHalf}`,
          newBalance: newBalanceHalf
        };

      case 'waive':
        // Waive the fee for this month
        await update(memberRef, {
          paymentStatus: 'waived',
          lastWaiverDate: new Date().toISOString()
        });
        return {
          success: true,
          message: 'Month fee waived successfully'
        };

      default:
        throw new Error('Invalid action specified');
    }

  } catch (error) {
    console.error('Error in handleNoShowMonth:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while handling no-show month'
    };
  }
};

// Get payment analytics and insights
export const getPaymentAnalytics = async (tenantId, memberId) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    
    const [memberSnapshot, paymentsSnapshot] = await Promise.all([
      get(memberRef),
      query(paymentsRef, orderByChild('memberId'), equalTo(memberId))
    ]);

    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    const payments = [];

    if (paymentsSnapshot.exists()) {
      paymentsSnapshot.forEach((childSnapshot) => {
        payments.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
    }

    // Calculate analytics
    const totalPaid = payments.reduce((sum, payment) => sum + (payment.paymentAmount || 0), 0);
    const averagePayment = payments.length > 0 ? totalPaid / payments.length : 0;
    const onTimePayments = payments.filter(p => p.type === PAYMENT_TYPES.FULL).length;
    const partialPayments = payments.filter(p => p.type === PAYMENT_TYPES.PARTIAL).length;
    const latePayments = payments.filter(p => p.type === PAYMENT_TYPES.LATE_FEE).length;

    // Payment trend analysis
    const monthlyTrends = {};
    payments.forEach(payment => {
      const date = new Date(payment.paymentDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyTrends[monthKey]) {
        monthlyTrends[monthKey] = {
          total: 0,
          count: 0,
          average: 0
        };
      }
      
      monthlyTrends[monthKey].total += payment.paymentAmount || 0;
      monthlyTrends[monthKey].count += 1;
      monthlyTrends[monthKey].average = monthlyTrends[monthKey].total / monthlyTrends[monthKey].count;
    });

    return {
      success: true,
      analytics: {
        memberId,
        memberName: member.fullName,
        currentBalance: member.currentBalance || 0,
        monthlyFee: parseFloat(member.fees.replace(/[^\d.]/g, '') || 0),
        totalPaid,
        averagePayment,
        totalPayments: payments.length,
        onTimePayments,
        partialPayments,
        latePayments,
        paymentRate: payments.length > 0 ? (onTimePayments / payments.length) * 100 : 0,
        monthlyTrends,
        lastPaymentDate: member.lastPaymentDate,
        nextDueDate: member.nextDueDate,
        paymentStatus: member.paymentStatus
      }
    };

  } catch (error) {
    console.error('Error in getPaymentAnalytics:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while getting payment analytics'
    };
  }
};

// Generate payment reminders based on balance and due date
export const generatePaymentReminders = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (!snapshot.exists()) {
      return { success: true, reminders: [] };
    }

    const now = new Date();
    const reminders = [];

    snapshot.forEach((childSnapshot) => {
      const member = childSnapshot.val();
      const memberId = childSnapshot.key;

      // Paused memberships accrue no fees, so they get no reminders
      if (isMembershipPaused(member)) {
        return;
      }

      const fees = calculateMembershipFees(member);

      // Anything charged but unpaid is already due
      if (fees.balanceDue > 0) {
        const dueDate = toDate(member.nextDueDate);
        const daysOverdue = dueDate ? Math.ceil((now - dueDate) / (1000 * 60 * 60 * 24)) : 0;

        reminders.push({
          memberId,
          memberName: member.fullName,
          type: 'overdue',
          daysOverdue: Math.max(0, daysOverdue),
          amount: fees.balanceDue,
          priority: 'high'
        });
      } else if (fees.nextChargeDate) {
        // Paid up - warn about the next cycle starting soon
        const daysUntilDue = Math.ceil((toDate(fees.nextChargeDate) - now) / (1000 * 60 * 60 * 24));

        if (daysUntilDue <= 7 && daysUntilDue > 0) {
          reminders.push({
            memberId,
            memberName: member.fullName,
            type: 'upcoming',
            daysUntilDue,
            amount: fees.feeAmount,
            priority: daysUntilDue <= 3 ? 'high' : 'medium'
          });
        }
      }
    });

    // Sort by priority and urgency
    reminders.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return a.type === 'overdue' ? -1 : 1;
    });

    return {
      success: true,
      reminders,
      totalReminders: reminders.length
    };

  } catch (error) {
    console.error('Error in generatePaymentReminders:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'An error occurred while generating payment reminders'
    };
  }
};

// Helper function to calculate next due date after payment
const calculateNextDueDateAfterPayment = (currentDueDate, membershipDuration) => {
  const dueDate = new Date(currentDueDate);
  const duration = membershipDuration.toLowerCase();
  
  switch (duration) {
    case '1 month':
    case 'monthly':
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate()).toISOString();
    case '3 months':
    case '3months':
    case 'quarterly':
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 3, dueDate.getDate()).toISOString();
    case '6 months':
    case '6months':
    case 'semi-annually':
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 6, dueDate.getDate()).toISOString();
    case '1 year':
    case 'annual':
    case 'annually':
    case 'yearly':
      return new Date(dueDate.getFullYear() + 1, dueDate.getMonth(), dueDate.getDate()).toISOString();
    default:
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate()).toISOString();
  }
};




